/**
 * AuthController.js
 *
 * Navigo-compatible authentication controller for StreamSyncEngage SaaS client.
 * Adapted from LaunchLegitController.js to work with Navigo router.
 *
 * @description
 * A static singleton class that provides:
 * - Centralized mvvLegit initialization and configuration
 * - Navigo router integration (instead of neodigmCarousel)
 * - Automatic auth ping checks with configurable intervals
 * - Deep-link GUID handling that survives account creation
 * - Entity state persistence in localStorage (plain JSON - no compression dependency)
 * - Admin-actuated password reset enforcement
 * - Server-notified signout with hard refresh
 *
 * @dependencies
 * - mvvLegit (from neodigm55 v5.0 library)
 * - Navigo router instance (passed during init)
 *
 * @author Scott Krause
 * @version 1.0.0-navigo
 * @license MIT
 */

import PubSub from 'pubsub-js';
import { trackSessionActivity } from './sessionActivity.js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { resolveCapabilities } from '@sse/shared';
import { allowEditorNavigation, suspendEditorSession, getSuspendedEditor, discardSuspendedEditor } from '../core/magnetEditorSession.js';
import { resetDeadSession, sessionFetch } from '../core/navGeneration.js';
import { shouldSuppressAuthRedirect } from '../core/publicLanding.js';

export default class AuthController {
    // ========================================================================
    // CONFIGURATION - Customize these for your project
    // ========================================================================

    static CONFIG = {
        // API Base URL - SaaS client ALWAYS uses production API
        // RULE: All client apps point to hosted API (no localhost fallback)
        BASE_URL: 'https://machfivemagnet-saas.onrender.com',

        // Auth ping interval (milliseconds) - 0 to disable
        FF_AUTH_PING_MS: 1440000, // 24 minutes

        // LocalStorage keys
        LS_KEYS: {
            ENTITY: 'sse_session_user',      // Entity state (matches existing key)
            DEEP_LINK: 'sse_deeplink',       // Deep-link GUID
            SCOPE: 'sse_session_scope',      // Cached tenancy scope (/me)
            ACTIVE_ORG: 'sse_active_org',    // Active org guid (org switcher)
            REMEMBERED_EMAIL: 'sse_remembered_email' // "Remember my email" on sign-in — email only, never the password
        },

        // Route names (Navigo paths without # prefix)
        ROUTES: {
            SIGNIN: 'signin',
            SIGNUP: 'signup',
            HOME: 'home',
            RESET_HASH: 'resethash',
            FORGOT: 'forgot',
            SPLASH: 'splash',
        },

        // Protected routes (require authentication)
        PROTECTED_ROUTES: [
            'home',
            'resethash',
        ],

        // API endpoints - m5t/v5 pattern
        ENDPOINTS: {
            SIGNIN: '/m5t/v5/acctEntity/signin',
            SIGNOUT: '/m5t/v5/acctEntity/signout',
            SIGNUP: '/m5t/v5/acctEntity/',
            RESET_HASH: '/m5t/v5/acctEntity/resetHash',
            RESET_FORGOT: '/m5t/v5/acctEntity/resetforgot',
            ME: '/m5t/v5/acctEntity/me',
            RENEW: '/m5t/v5/acctEntity/session/renew',
            PING: '/ping',
            WEBAUTHN_AUTH_OPTIONS: '/m5t/v5/webauthn/authenticate/options',
            WEBAUTHN_AUTH_VERIFY: '/m5t/v5/webauthn/authenticate/verify',
        },

        // Metering captions
        METER_CAPTIONS: {
            DEEP_LINK: 'deep_link_access',
            SIGNOUT_CLIENT: 'signout_client_initiated',
            SIGNOUT_SERVER: 'signout_server_initiated',
            PING_SUCCESS: 'ping_auth_success',
            PING_FAILURE: 'ping_auth_failure'
        }
    };

    // ========================================================================
    // PRIVATE PROPERTIES
    // ========================================================================

    static #initialized = false;
    static #stopActivity = null;
    static #renewingToken = false;
    static #pingInterval = null;
    static #entity = null;
    static #scope = null;            // Cached tenancy scope (/me response)
    static #scopeFetchPromise = null; // in-flight /me call, shared by concurrent callers (see fetchScope())
    static #sessionGeneration = 0;
    static #deepLinkGuid = null;
    static #navigateFunction = null; // Navigo navigate function

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    /**
     * Initialize the AuthController
     *
     * @param {Object} options - Configuration options
     * @param {string} options.BASE_URL - API base URL
     * @param {Function} options.navigateFunction - Navigo navigate function
     * @param {Object} options.customConfig - Optional custom configuration to merge
     * @returns {Promise<boolean>} - Success status
     */
    static async init({ BASE_URL, navigateFunction, customConfig = {} } = {}) {
        if (this.#initialized) {
            console.warn('[AuthController] Already initialized');
            return true;
        }

        try {
            // Store navigate function
            if (navigateFunction && typeof navigateFunction === 'function') {
                this.#navigateFunction = navigateFunction;
            } else {
                throw new Error('navigateFunction is required and must be a function');
            }

            // Merge custom config
            if (BASE_URL) {
                this.CONFIG.BASE_URL = BASE_URL;
            }
            if (customConfig && Object.keys(customConfig).length > 0) {
                this.CONFIG = { ...this.CONFIG, ...customConfig };
            }

            console.log('[AuthController] Initializing with config:', {
                BASE_URL: this.CONFIG.BASE_URL,
                PING_INTERVAL: this.CONFIG.FF_AUTH_PING_MS
            });

            // Load persisted entity state
            this.#loadEntityState();

            // Initialize mvvLegit
            this.#initMvvLegit();

            // Install the X-Active-Org header on every API request (after mvvLegit
            // so its Bearer-injecting fetch override composes underneath ours).
            this.#installActiveOrgFetch();
            this.#startSessionActivity();

            // Start auth ping if configured (will only ping when authenticated)
            if (this.CONFIG.FF_AUTH_PING_MS > 0) {
                this.#startAuthPing();
            }

            this.#initialized = true;
            console.log('[AuthController] Initialized successfully');
            return true;

        } catch (error) {
            console.error('[AuthController] Initialization failed:', error);
            return false;
        }
    }

    // ========================================================================
    // MVVLEGIT INITIALIZATION
    // ========================================================================

    /**
     * Initialize mvvLegit authentication framework
     */
    static #initMvvLegit() {
        // Check for mvvLegit (global variable, not window property)
        if (typeof mvvLegit === 'undefined' || !mvvLegit) {
            throw new Error('mvvLegit is not available. Ensure neodigm55 library is loaded.');
        }

        mvvLegit
            .setNavConroller((routeName = null) => {
                // mvvLegit navigation callback
                if (this.#renewingToken) return;
                if (routeName) {
                    // Public token-carrying landing (invite, questionnaire, ...): the
                    // page is valid for ANY auth state, so every mvvLegit-initiated
                    // redirect stays put while the visitor is on one — "unauth → go
                    // sign in" would bounce a recipient off the page they were emailed,
                    // and "authed → go home" yanked a signed-in reseller previewing
                    // their own link off to the dashboard ~2s after the form painted.
                    //
                    // Asks WHETHER this is a public landing — not WHICH one, and not
                    // WHERE the redirect points. Both distinctions are how this guard
                    // keeps dying:
                    //   - v1 tested the hash for '#/accept-invite', a route never
                    //     navigated to (these render at #/splash), so it never fired.
                    //   - v2 shared isInviteLink() with loadSplash — correct for invites,
                    //     but it enumerated one landing. When the questionnaire landing
                    //     was added to loadSplash, this silently did not cover it, and
                    //     every recipient got bounced to signin ~2s after their form
                    //     appeared (mvvLegit init resolving, not a timer).
                    //   - v4 enumerated destinations (splash/signin only), which let
                    //     the authed-viewer home redirect through. The invitee flow the
                    //     home-exception protected never needed it: doSignin() below
                    //     navigates home explicitly.
                    // core/publicLanding.js is now the single list both sides consult.
                    if (shouldSuppressAuthRedirect(routeName)) {
                        console.log('[AuthController] Ignoring mvvLegit redirect to', routeName, '— keeping visitor on their public landing page');
                        return;
                    }
                    console.log('[AuthController] mvvLegit navigation to:', routeName);
                    this.#navigate(routeName);
                }
            })
            .setOnState((state = null) => {
                // Token rotation must not remount a route or reset unsaved work.
                if (this.#renewingToken && (state === 4 || state === 'AUTH')) return;
                // State change callback
                if (state) {
                    console.log('[AuthController] Auth state changed:', state);

                    // Handle state-specific logic (supports both numeric and string states)
                    // mvvLegit v5.0 uses numeric states: 1=UNAUTH, 2=UNVERF, 4=AUTH
                    switch(state) {
                        case 'AUTH':
                        case 4:
                            this.#onAuthenticated();
                            break;
                        case 'UNAUTH':
                        case 1:
                            this.#onUnauthenticated();
                            break;
                        case 'UNVERF':
                        case 2:
                            this.#onUnverified();
                            break;
                    }
                }
            })
            .init({ BASE: this.CONFIG.BASE_URL });

        console.log('[AuthController] mvvLegit initialized');
    }

    /**
     * Navigate to route using Navigo
     *
     * @param {string} routeName - Route name (with or without _route suffix)
     */
    static #navigate(routeName) {
        if (!this.#navigateFunction) {
            console.error('[AuthController] Navigate function not set');
            return;
        }

        console.log('[AuthController] #navigate called with:', routeName);

        // Convert route token to Navigo path
        // Remove _route suffix and convert underscores to hyphens
        const path = routeName.replace(/_route$/, '').replace(/_/g, '-');

        console.log('[AuthController] Navigating to path:', path);
        this.#navigateFunction(path);
    }

    // ========================================================================
    // ACTIVE-ORG REQUEST CONTEXT
    // ========================================================================

    /**
     * Wrap window.fetch so every request to the API base carries the active-org
     * context as an X-Active-Org header. This is the SINGLE injection point (the
     * same pattern mvvLegit uses for the Bearer token) — route modules never have
     * to plumb an org param, and the server's attachScope middleware narrows
     * scope.ownerGuids to that org for every scoped endpoint at once.
     */
    static #installActiveOrgFetch() {
        if (window.__m5tActiveOrgFetch) return;
        const nativeFetch = window.fetch.bind(window);
        window.fetch = (input, init) => {
            try {
                const url = typeof input === 'string' ? input : (input?.url || '');
                const org = localStorage.getItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
                if (org && url.startsWith(this.CONFIG.BASE_URL)) {
                    const headers = new Headers(
                        (init && init.headers) || (input instanceof Request ? input.headers : undefined)
                    );
                    headers.set('X-Active-Org', org);
                    init = { ...(init || {}), headers };
                }
            } catch { /* never break fetch */ }
            return nativeFetch(input, init);
        };
        window.__m5tActiveOrgFetch = true;
        console.log('[AuthController] Active-org fetch header installed');
    }

    // ========================================================================
    // ENTITY STATE PERSISTENCE
    // ========================================================================

    /**
     * Load entity state from localStorage
     */
    static #loadEntityState() {
        try {
            const saved = localStorage.getItem(this.CONFIG.LS_KEYS.ENTITY);
            if (saved) {
                this.#entity = JSON.parse(saved);
                // Presence only — never the address itself. This ran on every
                // entity-state read, printing the signed-in user's real email
                // into the browser console (and into anything scraping it:
                // extensions, screen shares, support-session recordings).
                console.log('[AuthController] Entity state loaded; hasEmail:', Boolean(this.#entity?.email));
            }
        } catch (err) {
            console.error('[AuthController] Error loading entity state:', err);
            localStorage.removeItem(this.CONFIG.LS_KEYS.ENTITY);
            this.#entity = null;
        }

        // Load deep-link GUID
        this.#deepLinkGuid = localStorage.getItem(this.CONFIG.LS_KEYS.DEEP_LINK) || null;

        // Load cached tenancy scope (last-good; reconciled by fetchScope() on init)
        try {
            const savedScope = localStorage.getItem(this.CONFIG.LS_KEYS.SCOPE);
            if (savedScope) {
                this.#scope = JSON.parse(savedScope);
                console.log('[AuthController] Scope state loaded (cached):', this.#scope?.uiMode);
            }
        } catch (err) {
            console.error('[AuthController] Error loading scope state:', err);
            localStorage.removeItem(this.CONFIG.LS_KEYS.SCOPE);
            this.#scope = null;
        }
    }

    /**
     * Save entity state to localStorage
     *
     * @param {Object} entity - Entity object to save
     */
    static #saveEntityState(entity) {
        try {
            this.#entity = entity;
            localStorage.setItem(this.CONFIG.LS_KEYS.ENTITY, JSON.stringify(entity));
            console.log('[AuthController] Entity state saved');
        } catch (err) {
            console.error('[AuthController] Error saving entity state:', err);
        }
    }

    /**
     * Clear entity state
     */
    static #clearEntityState() {
        ++this.#sessionGeneration;
        this.#scopeFetchPromise = null;
        this.#entity = null;
        this.#scope = null;
        localStorage.removeItem(this.CONFIG.LS_KEYS.ENTITY);
        localStorage.removeItem(this.CONFIG.LS_KEYS.SCOPE);
        try {
            localStorage.removeItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
            sessionStorage.removeItem(this.#PREVIEW_KEY);
        } catch { /* ignore */ }
    }

    /**
     * Get entity state
     *
     * @returns {Object|null}
     */
    static getEntity() {
        return this.#entity;
    }

    // ========================================================================
    // TENANCY SCOPE  (/me → capabilities, uiMode, active org, memberships)
    // ========================================================================

    /**
     * Save tenancy scope to memory + localStorage (last-good cache).
     */
    static #saveScopeState(scope) {
        try {
            this.#scope = scope;
            localStorage.setItem(this.CONFIG.LS_KEYS.SCOPE, JSON.stringify(scope));
        } catch (err) {
            console.error('[AuthController] Error saving scope state:', err);
        }
    }

    /**
     * Fetch the caller's tenancy scope (/me) and cache it. mvvLegit injects the
     * Bearer token automatically for BASE_URL requests (same as #doPing).
     *
     * - 401/403 → hard signout (never render a logged-in nav for an invalid session)
     * - 404 / 5xx / network → keep last-good cached scope (cache-first); no signout
     *
     * Reconciliation, NOT a gate by default: callers should not block routing on
     * this — the cached scope paints first and AUTH.SCOPE.LOADED updates the UI
     * when it lands. When a caller DOES need to know the real thing landed
     * (main.js awaits this once, specifically when a stored ACTIVE_ORG exists —
     * see its own comment for why), concurrent callers share the SAME in-flight
     * promise (#scopeFetchPromise) rather than a boolean guard that would hand
     * an awaiting caller stale cached data before the real request finishes.
     *
     * @returns {Promise<Object|null>} scope object, cached scope on soft failure, or null
     */
    static async fetchScope() {
        // Only resolve scope for a genuinely authenticated (AUTH) session. UNVERF /
        // expired / signed-out states must NOT hit /me (a bare /me would 401 → a spurious
        // forced signout). isAuthenticated() (mvvLegit AUTH state, read from storage) is the
        // reliable signal: mvvLegit injects the Bearer from its OWN storage, and
        // getConf().token is NOT populated on a rehydrated session — so never gate on it.
        if (typeof mvvLegit === 'undefined' || !this.isAuthenticated()) {
            console.log('[AuthController] fetchScope skipped — not authenticated');
            return this.#scope;
        }
        // De-dupe concurrent calls onto the SAME in-flight request — #onAuthenticated()
        // and main.js's own boot-time call can both fire fetchScope() within the same
        // tick or two, and a caller that awaits this (main.js, when reconciling a
        // stored ACTIVE_ORG) needs the REAL completion, not an immediately-resolved
        // stale value.
        if (this.#scopeFetchPromise) return this.#scopeFetchPromise;
        const request = this.#doFetchScope().finally(() => { if (this.#scopeFetchPromise === request) this.#scopeFetchPromise = null; });
        this.#scopeFetchPromise = request;
        return request;
    }

    static async #doFetchScope() {
        const generation = this.#sessionGeneration;
        try {
            const activeOrg = localStorage.getItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
            const qs = activeOrg ? `?org=${encodeURIComponent(activeOrg)}` : '';
            const response = await sessionFetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.ME}${qs}`);
            if (generation !== this.#sessionGeneration) return null;

            if (response.status === 401) {
                // 401 only: /me has NO 403 left to answer with. It is mounted on
                // doTokenAuth alone (no attachScope), so it has no authorization step,
                // and its one remaining failure — a token naming an entity that no
                // longer exists — was moved to 401 in acctEntity.js as the second copy
                // of that check. Keeping `|| 403` would assert to the next reader that
                // /me can still refuse for a permission reason, which it cannot; and a
                // 403 appearing here in future would mean authorization, which is not
                // grounds for ending a session.
                console.warn('[AuthController] /me unauthorized — signing out');
                this.doSignout(true);
                return null;
            }

            if (!response.ok) {
                console.warn(`[AuthController] /me failed (${response.status}) — using cached scope`);
                return this.#scope;
            }

            const data = await response.json();
            if (generation !== this.#sessionGeneration) return null;
            if (!data?.ok) {
                console.warn('[AuthController] /me returned not-ok — using cached scope');
                return this.#scope;
            }

            // Re-sync the stored active org: if the server fell back to a different
            // org (stale localStorage, revoked client, OR a requested org the caller
            // has no direct membership/reseller-subtree relationship to —
            // data.activeOrgError), drop the stored value so data requests return to
            // the caller's default scope instead of 403ing. data.activeOrgError is
            // returned as-is below so the caller (whoever invoked setActiveOrg) can
            // show its own visible message — this service layer stays UI-agnostic.
            try {
                const stored = localStorage.getItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
                if (stored && data.activeOrg?.guid !== stored) {
                    console.warn(`[AuthController] Stored active org not honored by /me${data.activeOrgError ? ' (activeOrgError)' : ''} — clearing`);
                    localStorage.removeItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
                }
            } catch { /* ignore */ }

            this.#saveScopeState(data);
            PubSub.publish(AppBroadcast.AUTH_SCOPE_LOADED, data);
            console.log(`[AuthController] Scope loaded: ${data.uiMode} (${data.memberships?.length || 0} org(s), ${data.managedClients?.length || 0} client(s))`);
            return data;

        } catch (error) {
            if (generation !== this.#sessionGeneration) return null;
            console.error('[AuthController] fetchScope error:', error);
            return this.#scope; // cache-first; no signout on network error
        }
    }

    /**
     * Switch the active org: persist the choice, refetch /me (so uiMode +
     * capabilities reflect the new org) and re-publish AUTH.SCOPE.LOADED.
     */
    static async setActiveOrg(orgGuid) {
        if (!allowEditorNavigation('/magnets', { forceLeave: true })) return this.#scope;
        try {
            if (orgGuid) localStorage.setItem(this.CONFIG.LS_KEYS.ACTIVE_ORG, orgGuid);
            else localStorage.removeItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
        } catch { /* ignore */ }
        return this.fetchScope();
    }

    /** Full cached scope ({ entity, isPlatformAdmin, uiMode, capabilities, activeOrg, memberships, managedClients }). */
    static getScope() { return this.#scope; }
    /** Capabilities for the ACTIVE org (what the nav filters on). Preview-as-client masks these down. */
    static getCapabilities() {
        if (this.isPreviewingAsClient()) {
            // Pixel-true client experience: exactly what the managed client's owner
            // sees. Client-side mask only — the server keeps the caller's real
            // (larger) reseller rights, so this can only ever REMOVE ability.
            return resolveCapabilities({ orgChannel: 'managed_client', memberRole: 'owner' });
        }
        return this.#scope?.capabilities || [];
    }
    /** UI mode: 'admin' | 'reseller' | 'managed' | 'self_serve'. Preview-as-client forces 'managed'. */
    static getUiMode() {
        if (this.isPreviewingAsClient()) return 'managed';
        return this.#scope?.uiMode || 'self_serve';
    }
    /** Active org { guid, name, channel, memberRole, capabilities, viaSubtree } or null. */
    static getActiveOrg() { return this.#scope?.activeOrg || null; }
    /** All org memberships [{ orgGuid, channel, memberRole, capabilities }]. */
    static getMemberships() { return this.#scope?.memberships || []; }
    /** Reseller subtree: managed-client orgs [{ orgGuid, name, channel, parent }]. */
    static getManagedClients() { return this.#scope?.managedClients || []; }
    /** True when managedClients is the caller's ASSIGNED slice, not the whole book. */
    static isClientScopeRestricted() { return !!this.#scope?.clientScopeRestricted; }
    /** The persisted EXPLICIT org choice (null = default scope / reseller portfolio). */
    static getStoredActiveOrg() {
        try { return localStorage.getItem(this.CONFIG.LS_KEYS.ACTIVE_ORG); } catch { return null; }
    }

    // ---- Preview-as-client (demo mode) --------------------------------------
    // Session-scoped toggle available while acting inside a subtree client's
    // context: the UI renders with the client's own capability set + uiMode so a
    // reseller can screen-share exactly what their client sees.

    static #PREVIEW_KEY = 'm5m_preview_as_client';

    static isPreviewingAsClient() {
        try {
            return sessionStorage.getItem(this.#PREVIEW_KEY) === '1'
                && !!this.#scope?.activeOrg?.viaSubtree;
        } catch { return false; }
    }

    static setPreviewAsClient(on) {
        try {
            if (on) sessionStorage.setItem(this.#PREVIEW_KEY, '1');
            else sessionStorage.removeItem(this.#PREVIEW_KEY);
        } catch { /* ignore */ }
        // Re-publish the (unchanged) scope so the nav + current route re-render
        // through the masked accessors.
        if (this.#scope) PubSub.publish(AppBroadcast.AUTH_SCOPE_LOADED, this.#scope);
    }

    // ========================================================================
    // AUTHENTICATION METHODS
    // ========================================================================

    /** Accept only the explicit Shopify redemption response; caller then reloads the selected workspace. */
    static acceptShopifySignin(data) {
        if(!data?.ok||!data.accessToken||!data.entity?.guid||!data.workspace?.guid)
            throw new Error('Shopify sign-in did not return a complete account. Try again.');
        // The public landing holds automatic MVV navigation until context is saved.
        this.#clearEntityState();
        resetDeadSession();
        localStorage.setItem(this.CONFIG.LS_KEYS.ACTIVE_ORG,data.workspace.guid);
        this.#saveEntityState(data.entity);
        mvvLegit.doSignin(data.accessToken);
    }

    /**
     * Sign in a user
     *
     * @param {string} email - User email
     * @param {string} password - User's plaintext password, sent over TLS
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doSignin(email, password) {
        try {
            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.SIGNIN}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            // Handle 303 - Password reset required
            if (response.status === 303) {
                console.warn('[AuthController] Password reset required');

                // Store token and entity for reset flow
                if (data.accessToken) {
                    mvvLegit.doSignin(data.accessToken);
                    this.#saveEntityState(data.entity);
                }

                // Navigate to reset password page
                setTimeout(() => {
                    this.#navigate(this.CONFIG.ROUTES.RESET_HASH);
                }, 1500);

                return { ok: false, resetRequired: true, data };
            }

            if (response.ok && data.accessToken) {
                console.log('[AuthController] Signin successful, calling mvvLegit.doSignin()');

                // Store token via mvvLegit (this should trigger setNavConroller callback)
                mvvLegit.doSignin(data.accessToken);

                // Persist entity state
                this.#saveEntityState(data.entity);

                // A fresh sign-in always lands on the caller's DEFAULT scope —
                // for a reseller that's the portfolio view, not whichever client
                // context a previous session left behind.
                try {
                    localStorage.removeItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
                    sessionStorage.removeItem(this.#PREVIEW_KEY);
                } catch { /* ignore */ }

                // Resolve tenancy scope (capabilities/uiMode/orgs) — non-blocking;
                // publishes AUTH.SCOPE.LOADED when it lands so the nav can re-render.
                this.fetchScope();

                // Explicitly navigate to home route (don't rely on callback)
                console.log('[AuthController] Explicitly navigating to home');
                this.#navigate(this.CONFIG.ROUTES.HOME);

                return { ok: true, data };
            }

            // Sign in failed
            return { ok: false, error: data.error || 'Sign in failed', data };

        } catch (error) {
            console.error('[AuthController] Signin error:', error);
            return { ok: false, error: error.message };
        }
    }

    /**
     * Sign in with a passkey (WebAuthn) — no email typed first. Discoverable
     * credentials let the browser offer up whichever passkey it has for this
     * site; the server resolves which account it belongs to from the
     * assertion itself. Offered alongside doSignin(), not a replacement.
     *
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doPasskeySignin() {
        if (!window.PublicKeyCredential) {
            return { ok: false, error: 'Passkeys are not supported in this browser' };
        }
        try {
            const { startAuthentication } = await import('@simplewebauthn/browser');

            const optRes = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.WEBAUTHN_AUTH_OPTIONS}`, { method: 'POST' });
            const optData = await optRes.json();
            if (!optRes.ok || !optData.ok) {
                return { ok: false, error: optData.error || 'Could not start passkey sign-in' };
            }

            let credential;
            try {
                credential = await startAuthentication({ optionsJSON: optData.options });
            } catch (err) {
                // NotAllowedError covers both "user canceled" and "no passkey
                // available/timed out" — the two ordinary declines, not worth
                // logging as an error.
                const canceled = err?.name === 'NotAllowedError';
                if (!canceled) console.error('[AuthController] Passkey ceremony error:', err);
                return { ok: false, canceled, error: canceled ? 'Passkey sign-in was canceled' : (err.message || 'Passkey sign-in failed') };
            }

            const verifyRes = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.WEBAUTHN_AUTH_VERIFY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ response: credential }),
            });
            const data = await verifyRes.json();

            if (!verifyRes.ok || !data.ok || !data.accessToken) {
                return { ok: false, error: data.error || 'Passkey sign-in failed', data };
            }

            // Same post-signin sequence as doSignin() — a passkey-issued
            // session isn't treated any differently once a token exists.
            mvvLegit.doSignin(data.accessToken);
            this.#saveEntityState(data.entity);
            try {
                localStorage.removeItem(this.CONFIG.LS_KEYS.ACTIVE_ORG);
                sessionStorage.removeItem(this.#PREVIEW_KEY);
            } catch { /* ignore */ }
            this.fetchScope();
            this.#navigate(this.CONFIG.ROUTES.HOME);

            return { ok: true, data };
        } catch (error) {
            console.error('[AuthController] Passkey signin error:', error);
            return { ok: false, error: error.message };
        }
    }

    /**
     * Sign out current user
     *
     * @param {boolean} serverInitiated - True if signout was server-initiated (e.g., expired token)
     */
    static async doSignout(serverInitiated = false) {
        console.log('[AuthController] doSignout called, serverInitiated:', serverInitiated);
        if (!serverInitiated) {
            if (!allowEditorNavigation('/signout', { forceLeave: true })) return;
            if (getSuspendedEditor() && !confirm('Discard your unfinished magnet changes and sign out?')) return;
            discardSuspendedEditor();
        }
        this.#stopActivity?.();
        const preserveEditor = serverInitiated && suspendEditorSession();

        try {
            // CRITICAL ORDER:
            // 1. Get token BEFORE calling mvvLegit.doSignout() (which clears it)
            // 2. Call server API BEFORE mvvLegit.doSignout() (which triggers navigation)
            // 3. THEN clear state

            const token = this.#sessionToken() || mvvLegit.getConf()?.token;
            console.log('[AuthController] Token exists:', !!token);

            // Call server signout endpoint if user-initiated and has token
            if (!serverInitiated && token) {
                const signoutUrl = `${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.SIGNOUT}?token=${encodeURIComponent(token)}`;
                console.log('[AuthController] Sending signout beacon');

                // Use navigator.sendBeacon() - specifically designed for analytics during page unload
                // It's the most reliable way to ensure the request completes even after hard refresh
                if (navigator.sendBeacon) {
                    const sent = navigator.sendBeacon(signoutUrl);
                    console.log('[AuthController] Beacon sent:', sent);

                    if (!sent) {
                        console.warn('[AuthController] Beacon failed - browser may be blocking it');
                    }
                } else {
                    console.warn('[AuthController] navigator.sendBeacon not supported, signout not metered');
                }
            } else if (!token) {
                console.warn('[AuthController] No token found for signout request');
            }

            // NOW clear state (after API call completes)
            console.log('[AuthController] Clearing mvvLegit state...');
            mvvLegit.doSignout();

            // Set signout timestamp after mvv is cleared
            console.log('[AuthController] Setting signout timestamp...');
            localStorage.setItem('mvv_ts', Date.now().toString());

            console.log('[AuthController] Clearing entity state...');
            this.#clearEntityState();

            console.log('[AuthController] Stopping auth ping...');
            this.#stopAuthPing();

            // Reauthentication can happen in this SPA. Reloading would destroy
            // the detached working copy, including incomplete fields and files.
            if (preserveEditor) { this.#navigate(this.CONFIG.ROUTES.SIGNIN); return; }

            // Hard refresh AFTER ensuring signout request completed
            // Give it a moment for any final operations
            setTimeout(() => {
                console.log('[AuthController] Performing hard refresh...');
                if (typeof neodigmUtils !== 'undefined' && neodigmUtils.hardReload) {
                    neodigmUtils.hardReload();
                } else {
                    window.location.reload();
                }
            }, 500);

        } catch (error) {
            console.error('[AuthController] Signout error:', error);
            if (preserveEditor) { this.#clearEntityState(); this.#stopAuthPing(); this.#navigate(this.CONFIG.ROUTES.SIGNIN); return; }

            // Force hard refresh even on error
            setTimeout(() => {
                if (typeof neodigmUtils !== 'undefined' && neodigmUtils.hardReload) {
                    neodigmUtils.hardReload();
                } else {
                    window.location.reload();
                }
            }, 500);
        }
    }

    /**
     * Sign up new user
     *
     * @param {Object} userData - { email, hash, first, last, company, phone }
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doSignup(userData) {
        try {
            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.SIGNUP}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData)
            });

            const data = await response.json();

            if (response.ok && data.accessToken) {
                console.log('[AuthController] Signup successful - user needs to verify email');

                // DO NOT sign in automatically - user must verify email first
                // Save entity state for display purposes only (no token stored)
                this.#saveEntityState(data.entity);

                // Return success without signing in
                return { ok: true, data };
            }

            return { ok: false, error: data.error || 'Sign up failed', data };

        } catch (error) {
            console.error('[AuthController] Signup error:', error);
            return { ok: false, error: error.message };
        }
    }

    /**
     * Reset password (authenticated)
     *
     * @param {string} email - User email
     * @param {string} password - New plaintext password, sent over TLS
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doResetHash(email, password) {
        try {
            const token = mvvLegit.getConf()?.token;

            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.RESET_HASH}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password, modified_by: email })
            });

            const data = await response.json();

            if (response.ok && data.ok) {
                console.log('[AuthController] Password reset successful');

                // Sign out to force re-login with new password
                await this.doSignout();

                return { ok: true, data };
            }

            return { ok: false, error: data.error || 'Password reset failed', data };

        } catch (error) {
            console.error('[AuthController] Reset password error:', error);
            return { ok: false, error: error.message };
        }
    }

    static #sessionToken() {
        try {
            if (!this.isAuthenticated()) return null;
            const key = mvvLegit.getConf()?.LSKEY || 'mvv';
            return JSON.parse(LZString.decompressFromUTF16(localStorage.getItem(key)))?.token || null;
        } catch { return null; }
    }

    static #startSessionActivity() {
        this.#stopActivity?.();
        this.#stopActivity = trackSessionActivity({
            getToken: () => this.#sessionToken(),
            renew: () => sessionFetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.RENEW}`, { method: 'POST' }),
            accept: token => this.acceptRenewedSession(token),
            expire: () => this.doSignout(true),
        });
    }

    static acceptRenewedSession(token) {
        if (!this.isAuthenticated() || typeof token !== 'string' || !token) return;
        // Use MVV's own persistence, with its synchronous auth/nav callbacks
        // suppressed only for this rotation. Account, workspace and editor stay put.
        this.#renewingToken = true;
        try { mvvLegit.doSignin(token); }
        finally { this.#renewingToken = false; }
    }

    // ========================================================================
    // AUTH PING
    // ========================================================================

    /**
     * Start periodic auth ping checks
     */
    static #startAuthPing() {
        if (this.#pingInterval) {
            console.log('[AuthController] Clearing existing ping interval');
            clearInterval(this.#pingInterval);
        }

        this.#pingInterval = setInterval(() => {
            console.log(`[AuthController] ⏰ Ping interval fired at ${new Date().toLocaleTimeString()}`);
            this.#doPing();
        }, this.CONFIG.FF_AUTH_PING_MS);

        console.log(`[AuthController] Auth ping started (${this.CONFIG.FF_AUTH_PING_MS}ms) - interval ID: ${this.#pingInterval}`);
        console.log(`[AuthController] Next ping scheduled for: ${new Date(Date.now() + this.CONFIG.FF_AUTH_PING_MS).toLocaleTimeString()}`);
    }

    /**
     * Stop auth ping checks
     */
    static #stopAuthPing() {
        if (this.#pingInterval) {
            clearInterval(this.#pingInterval);
            this.#pingInterval = null;
            console.log('[AuthController] Auth ping stopped');
        }
    }

    /**
     * Perform auth ping check
     *
     * Session-scoped rather than route-scoped; old replies cannot end a new login.
     */
    static async #doPing() {
        const generation = this.#sessionGeneration;
        try {
            const response = await sessionFetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.PING}`);
            if (generation !== this.#sessionGeneration) return;

            if (response.ok) {
                // Auth still valid
                console.log('[AuthController] Ping success');
            } else if (response.status === 401) {
                // 401 ONLY, deliberately — and the omission of 403 is the point.
                //
                // /ping is mounted with doTokenAuth and nothing else (server.js:662), and
                // since fix/token-auth-401 every authentication failure there — missing,
                // invalid, expired, revoked — answers 401. There is no longer any path by
                // which /ping returns 403 for an auth reason, so `|| 403` described a case
                // that cannot occur. Left in place it would tell the next reader that
                // /ping can still 403 for auth, which is exactly the ambiguity the server
                // change removed; and if a 403 ever DID appear here it would mean
                // authorization, which is not grounds for ending a session.
                console.warn('[AuthController] Auth ping failed - signing out');
                this.doSignout(true);
            }
        } catch (error) {
            console.error('[AuthController] Ping error:', error);
        }
    }

    // ========================================================================
    // DEEP LINKING
    // ========================================================================

    /**
     * Store deep-link GUID for later use
     *
     * @param {string} guid - GUID to store
     */
    static setDeepLinkGuid(guid) {
        this.#deepLinkGuid = guid;
        localStorage.setItem(this.CONFIG.LS_KEYS.DEEP_LINK, guid);
        console.log('[AuthController] Deep-link GUID stored:', guid);
    }

    /**
     * Get stored deep-link GUID
     *
     * @returns {string|null}
     */
    static getDeepLinkGuid() {
        return this.#deepLinkGuid;
    }

    /**
     * Clear deep-link GUID
     */
    static clearDeepLink() {
        this.#deepLinkGuid = null;
        localStorage.removeItem(this.CONFIG.LS_KEYS.DEEP_LINK);
    }

    // ========================================================================
    // STATE CALLBACKS
    // ========================================================================

    /**
     * Called when user becomes authenticated
     */
    static #onAuthenticated() {
        console.log('[AuthController] User authenticated');
        this.#startSessionActivity();
        ++this.#sessionGeneration;
        this.#scopeFetchPromise = null;
        resetDeadSession();

        // Start auth ping
        if (this.CONFIG.FF_AUTH_PING_MS > 0) {
            this.#startAuthPing();
        }

        // REGRESSION GUARD (sign-out-on-refresh — the REAL cause, not the stale-
        // ACTIVE_ORG race guarded above in main.js): mvvLegit.init() restores a
        // persisted AUTH session by calling THIS callback SYNCHRONOUSLY, from
        // inside its own init() body — via #changeState(oTJO.state) — and only
        // patches window.fetch to attach the Bearer token (#overFetch()) as the
        // LAST statement of that same init() call, further down the SAME
        // synchronous call stack. Calling fetchScope() directly from here fires
        // its /me request through the native, still-unpatched fetch — no
        // Authorization header at all — which the server correctly 401s, and
        // #doFetchScope() (correctly, taken in isolation) treats that self-
        // inflicted 401 as a dead session and signs out. This reproduces on
        // EVERY cold boot/refresh with a persisted session, deterministically —
        // it's a fixed code-ordering issue in mvvLegit's own init(), not a
        // timing-sensitive race, which is why it never showed up as
        // "intermittent." Deferring one microtask is enough: mvvLegit.init()'s
        // synchronous body (including the #overFetch() patch) always finishes
        // before any queued microtask runs.
        queueMicrotask(() => this.fetchScope());
    }

    /**
     * Called when user becomes unauthenticated
     */
    static #onUnauthenticated() {
        console.log('[AuthController] User unauthenticated');
        this.#stopActivity?.();
        ++this.#sessionGeneration;
        this.#scopeFetchPromise = null;
        // mvvLegit's own interceptor can sign out before AuthController sees
        // the response. Capture the editor before its navigation callback runs.
        if (suspendEditorSession()) this.#clearEntityState();

        // Stop auth ping
        this.#stopAuthPing();
    }

    /**
     * Called when user is unverified
     */
    static #onUnverified() {
        console.log('[AuthController] User unverified');
    }

    // ========================================================================
    // UTILITY METHODS
    // ========================================================================

    /**
     * Get current authentication state
     *
     * @returns {string|null} - 'AUTH', 'UNAUTH', 'UNVERF', or null
     */
    static getAuthState() {
        // mvvLegit doesn't expose getState() - read from localStorage instead
        try {
            const lsKey = mvvLegit.getConf()?.LSKEY || 'mvv';
            const compressed = localStorage.getItem(lsKey);
            if (!compressed) return 'UNAUTH';

            if (typeof LZString === 'undefined') return null;

            const decompressed = LZString.decompressFromUTF16(compressed);
            const data = JSON.parse(decompressed);
            const state = data?.state;

            // Convert numeric states to strings
            if (state === 1) return 'UNAUTH';
            if (state === 2) return 'UNVERF';
            if (state === 4) return 'AUTH';
            if (state === 5) return 'FORGOT';

            return null;
        } catch (err) {
            return null;
        }
    }

    /**
     * Check if user is authenticated
     *
     * @returns {boolean}
     */
    static isAuthenticated() {
        return this.getAuthState() === 'AUTH';
    }

    /**
     * Get configuration
     *
     * @returns {Object}
     */
    static getConfig() {
        return this.CONFIG;
    }

    /**
     * Navigate to route by name
     *
     * @param {string} routeName - Route name (e.g., 'signin', 'home')
     */
    static navigateTo(routeName) {
        this.#navigate(routeName);
    }

    /**
     * DEBUG: Check if ping interval is active
     *
     * @returns {boolean}
     */
    static isPingActive() {
        return this.#pingInterval !== null;
    }

    /**
     * DEBUG: Manually trigger a ping now
     */
    static async triggerPingNow() {
        console.log('[AuthController] DEBUG: Manually triggering ping...');
        await this.#doPing();
    }
}
